
import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../contexts/AppContext';
import * as d3 from 'd3';
import { Download, X, CheckSquare, ZoomIn, ZoomOut, Maximize, MousePointerClick, Move } from 'lucide-react';
import { StepProgressIndicator } from '../components/WorkflowNavigation';
import {
    useGraphData,
    useNodeSelection,
    NODE_TYPE_MAP,
    NODE_STYLE_MAP,
    LINK_COLORS,
    GraphNodeKind,
    D3Node,
    D3Link
} from '../hooks/useGraphVisualization';
import { downloadCSVWithAudit, ExportAuditMetadata } from '../utils/exportUtils';

const ExportPage: React.FC = () => {
    const { allData } = useAppContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const gRef = useRef<SVGGElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Use shared hooks for data and selection
    const { allNodes, allLinks } = useGraphData();
    const { selectedNodes, toggleNodeSelection, toggleColumnSelection, selectAll } = useNodeSelection(allNodes, allLinks);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userName, setUserName] = useState('');
    const [userInitials, setUserInitials] = useState('');

    // D3 State
    const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);


    // --- SETUP & RESIZE ---
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height });
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    // --- INITIALIZATION (Zoom & Static Layout) ---
    useEffect(() => {
        if (allNodes.length === 0 || dimensions.width === 0 || !svgRef.current) return;

        const svg = d3.select(svgRef.current);

        // Setup Zoom
        if (!zoomRef.current) {
            zoomRef.current = d3.zoom<SVGSVGElement, unknown>()
                .scaleExtent([0.1, 4])
                .on('zoom', (event) => {
                    d3.select(gRef.current).attr('transform', event.transform);
                });
            svg.call(zoomRef.current).on("dblclick.zoom", null);
        }

        // Define Markers
        const defs = svg.select('defs').size() ? svg.select('defs') : svg.append('defs');
        defs.selectAll('*').remove();
        LINK_COLORS.forEach(color => {
            const safeId = color.startsWith('var') ? (color.includes('prev') ? 'prev' : 'det') : color.replace('#', '');
            defs.append("marker")
                .attr("id", `arrow-export-${safeId}`)
                .attr("viewBox", "0 -5 10 10")
                .attr("refX", 14)
                .attr("refY", 0)
                .attr("markerWidth", 6)
                .attr("markerHeight", 6)
                .attr("orient", "auto")
                .append("path")
                .attr("d", "M0,-5L10,0L0,5")
                .attr("fill", color);
        });

    }, [dimensions.width]);

    // --- RENDER & UPDATE LOOP ---
    useEffect(() => {
        if (!gRef.current || allNodes.length === 0 || dimensions.width === 0) return;

        const g = d3.select(gRef.current);

        // 1. Calculate Grid Positions
        const colWidth = 320;
        const rowHeight = 70;
        const columnsOrder: GraphNodeKind[] = ['p', 'f', 'c-prev', 'c-det', 'd'];
        const columns = new Map(columnsOrder.map(k => [k, [] as D3Node[]]));
        allNodes.forEach(n => columns.get(n.kind)?.push(n));

        const totalGridWidth = columnsOrder.length * colWidth;
        const startX = Math.max(50, dimensions.width / 2 - totalGridWidth / 2 + colWidth / 2);

        columnsOrder.forEach((kind, colIndex) => {
            const list = columns.get(kind)!;
            const xPos = startX + colIndex * colWidth;
            const startY = 100;

            list.forEach((n, i) => {
                n.x = xPos;
                n.y = startY + (i * rowHeight);
            });
        });

        // 2. Draw Links
        const links = g.select(".links").selectAll<SVGPathElement, D3Link>("path")
            .data(allLinks, (d: any) => `${(d.source as D3Node).id || d.source}-${(d.target as D3Node).id || d.target}`);

        links.join("path")
            .attr("d", d => {
                const s = allNodes.find(n => n.id === ((d.source as any).id || d.source));
                const t = allNodes.find(n => n.id === ((d.target as any).id || d.target));
                if (!s || !t) return "";
                return `M${s.x},${s.y}L${t.x},${t.y}`;
            })
            .attr("fill", "none")
            .attr("stroke", d => d.color)
            .attr("stroke-dasharray", d => d.dash || null)
            .attr("marker-end", d => {
                const color = d.color;
                const safeId = color.startsWith('var') ? (color.includes('prev') ? 'prev' : 'det') : color.replace('#', '');
                return `url(#arrow-export-${safeId})`;
            })
            .transition().duration(300)
            .style("opacity", d => {
                const sId = (d.source as any).id || d.source;
                const tId = (d.target as any).id || d.target;
                return selectedNodes.has(sId) && selectedNodes.has(tId) ? 1 : 0.15;
            })
            .attr("stroke-width", d => {
                const sId = (d.source as any).id || d.source;
                const tId = (d.target as any).id || d.target;
                return selectedNodes.has(sId) && selectedNodes.has(tId) ? 2.5 : 1;
            });

        // 3. Draw Nodes
        const nodes = g.select(".nodes").selectAll<SVGGElement, D3Node>("g.node-group")
            .data(allNodes, d => d.id);

        const nodesEnter = nodes.enter().append("g")
            .attr("class", "node-group")
            .style("cursor", "pointer");

        nodesEnter.merge(nodes as any)
            .attr("transform", d => `translate(${d.x},${d.y})`)
            .on("click", (e, d) => {
                // Use shift key for branch selection, regular click for single toggle
                toggleNodeSelection(d.id, e.shiftKey || true); // Forced to true for now per user request ("just select one note and the related tree")
            })
            .each(function (d) {
                const el = d3.select(this);
                const isSelected = selectedNodes.has(d.id);

                const style = NODE_STYLE_MAP[d.kind];

                el.selectAll('*').remove();

                // Card Body
                el.append('rect')
                    .attr('width', d.w).attr('height', d.h).attr('x', -d.w / 2).attr('y', -d.h / 2)
                    .attr('rx', 12).attr('ry', 12)
                    .attr('fill', style.fill)
                    .attr('stroke', style.stroke)
                    .attr('stroke-width', isSelected ? 3 : 1.5)
                    .style('opacity', isSelected ? 1 : 0.5)
                    .style('filter', isSelected ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))' : 'none');

                // Label
                el.append('text')
                    .text(d.text.length > 35 ? d.text.substring(0, 32) + '...' : d.text)
                    .attr('dy', 5).attr('dx', 12)
                    .attr('text-anchor', 'middle')
                    .attr('font-size', '12px')
                    .style('font-family', 'sans-serif')
                    .style('fill', style.text)
                    .style('font-weight', isSelected ? 'bold' : 'normal')
                    .style('pointer-events', 'none');

                // Checkbox 
                const checkX = -d.w / 2 + 16;
                el.append('circle')
                    .attr('cx', checkX).attr('cy', 0)
                    .attr('r', 9)
                    .attr('fill', isSelected ? style.stroke : '#fff')
                    .attr('stroke', style.stroke)
                    .attr('stroke-width', 1.5);

                if (isSelected) {
                    el.append('path')
                        .attr('d', `M${checkX - 3.5},-1 L${checkX - 0.5},3 L${checkX + 4.5},-4`)
                        .attr('stroke', '#fff')
                        .attr('stroke-width', 2)
                        .attr('fill', 'none');
                }
            });

        nodes.exit().remove();

    }, [allNodes, allLinks, dimensions, selectedNodes, toggleNodeSelection]);


    // --- ZOOM CONTROLS ---
    const handleZoom = (factor: number) => {
        if (!zoomRef.current || !svgRef.current) return;
        d3.select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, factor);
    };
    const handleResetZoom = () => {
        if (!zoomRef.current || !svgRef.current) return;
        d3.select(svgRef.current).transition().duration(750).call(zoomRef.current.transform, d3.zoomIdentity);
    };


    // --- EXPORT ---
    const handleExport = () => {
        if (selectedNodes.size === 0) { alert("Select at least one item."); return; }
        const selectedFmeaIds = new Set<string>();
        allData.fmeaItems.forEach(f => {
            if (selectedNodes.has(f.id)) selectedFmeaIds.add(f.id);
            if (selectedNodes.has(f.function) ||
                (f.preventionControls && Array.isArray(f.preventionControls) && f.preventionControls.some(c => selectedNodes.has(c))) ||
                (f.detectionControls && Array.isArray(f.detectionControls) && f.detectionControls.some(c => selectedNodes.has(c)))) {
                selectedFmeaIds.add(f.id);
            }
        });
        const dataToExport = allData.fmeaItems.filter(f => selectedFmeaIds.has(f.id));
        if (JSON.stringify(dataToExport).length > 100000) { alert("Export too large."); return; }
        setIsModalOpen(true);
    };

    const confirmAndDownload = () => {
        if (!userName || !userInitials) { alert("Please sign off."); return; }

        const selectedFmeaIds = new Set<string>();
        allData.fmeaItems.forEach(f => {
            if (selectedNodes.has(f.id) || selectedNodes.has(f.function) ||
                (f.preventionControls && Array.isArray(f.preventionControls) && f.preventionControls.some(c => selectedNodes.has(c))) ||
                (f.detectionControls && Array.isArray(f.detectionControls) && f.detectionControls.some(c => selectedNodes.has(c)))) {
                selectedFmeaIds.add(f.id);
            }
        });
        const dataToExport = allData.fmeaItems.filter(f => selectedFmeaIds.has(f.id));

        const headers = Object.keys(dataToExport[0] || {}).join(',');
        const rows = dataToExport.map(row => Object.values(row).map(v => Array.isArray(v) ? `"${v.join(';')}"` : `"${String(v).replace(/"/g, '""')}"`).join(','));

        const metadata: ExportAuditMetadata = {
            exporterUid: `${userName} (${userInitials})`,
            organization: (allData.projectMetadata as any)?.organization || 'unknown',
            timestamp: new Date().toISOString()
        };

        downloadCSVWithAudit(
            `export_${new Date().toISOString().slice(0, 10)}.csv`,
            headers,
            rows,
            metadata
        );

        setIsModalOpen(false);
    };

    return (
        <div className="h-full flex flex-col gap-4 p-4">
            <div className="flex justify-between items-center bg-card p-4 rounded-lg border border-border shadow-sm">
                <div className="flex items-center gap-6">
                    <div>
                        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                            <Download size={24} className="text-blue-500" />
                            Export Manager
                        </h1>
                        <p className="text-text-secondary text-sm mt-1 mb-2">AIAG-VDA Step 7: Results Documentation</p>
                    </div>
                    <div className="hidden lg:block">
                        <StepProgressIndicator currentStep={7} completedSteps={[1, 2, 3, 4, 5, 6]} />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex bg-background border border-border rounded-lg p-1">
                        {(Object.keys(NODE_TYPE_MAP) as GraphNodeKind[]).map(kind => (
                            <button key={kind} onClick={() => toggleColumnSelection(kind)} className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors">
                                {NODE_TYPE_MAP[kind]}
                            </button>
                        ))}
                    </div>
                    <button onClick={selectAll} className="px-3 py-1.5 text-xs font-medium bg-background border border-border rounded-lg hover:bg-slate-100 transition-colors">Select All</button>
                    <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all shadow-sm">
                        <Download size={18} /> Export CSV
                    </button>
                </div>
            </div>

            <div className="flex-1 bg-card border border-border rounded-lg shadow-sm relative overflow-hidden" ref={containerRef}>
                <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                    <button onClick={() => handleZoom(1.2)} className="p-2 bg-white dark:bg-slate-800 border border-border rounded-md shadow-sm hover:bg-slate-50"><ZoomIn size={18} /></button>
                    <button onClick={() => handleZoom(0.8)} className="p-2 bg-white dark:bg-slate-800 border border-border rounded-md shadow-sm hover:bg-slate-50"><ZoomOut size={18} /></button>
                    <button onClick={handleResetZoom} className="p-2 bg-white dark:bg-slate-800 border border-border rounded-md shadow-sm hover:bg-slate-50"><Maximize size={18} /></button>
                </div>
                <div className="absolute top-4 left-4 z-10 pointer-events-none bg-card/90 backdrop-blur p-2 rounded border border-border text-xs text-text-secondary shadow-sm">
                    <div className="flex items-center gap-2"><MousePointerClick size={14} /> Click to select branch</div>
                    <div className="flex items-center gap-2 mt-1"><Move size={14} /> Drag to Pan, Scroll to Zoom</div>
                </div>

                <svg ref={svgRef} className="w-full h-full cursor-move bg-slate-50 dark:bg-slate-900/50">
                    <g ref={gRef}>
                        <g className="links"></g>
                        <g className="nodes"></g>
                    </g>
                </svg>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-sm flex flex-col border border-border">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-slate-50 dark:bg-slate-800/50 rounded-t-xl">
                            <h2 className="text-lg font-bold text-text-primary">Confirm Export</h2>
                            <button onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-slate-200 dark:hover-bg-slate-700 rounded-full"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-text-secondary">Exporting <strong>{selectedNodes.size}</strong> items.</p>
                            <div><label className="text-xs font-medium uppercase">Name</label><input type="text" value={userName} onChange={e => setUserName(e.target.value)} className="w-full mt-1 p-2 border rounded-md" /></div>
                            <div><label className="text-xs font-medium uppercase">Initials</label><input type="text" value={userInitials} onChange={e => setUserInitials(e.target.value)} className="w-full mt-1 p-2 border rounded-md" maxLength={4} /></div>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-border flex justify-end">
                            <button onClick={confirmAndDownload} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 shadow-sm">
                                <CheckSquare size={18} /> Download
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExportPage;
